import {
  ToastType,
  TOAST_IDS,
  PermissionsEnum,
  UserStatus,
  PermissionLevel,
} from "@/modules/common/types";
import {
  BULK_MOVE_LOCK_MESSAGE,
  getErrorMessage,
  isBulkMoveConflictError,
  isOlderThanXDays,
  showToast,
  userHasPermission,
} from "@/modules/common/utils";
import { LeadPageContext } from "@/modules/lead-page/context/lead-page.context";
import { queryClient } from "@/queryClient";
import { useContext, useEffect, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { checkLeadsSalesProgress } from "@/modules/leads/api/index.v2";
import { assignLeadV2 } from "@/modules/lead-page/api/lead-page.v2.api";
import useUsersData from "@/modules/common/hooks/fetch-users-data.hook";
import { NawyPassportContext } from "@/modules/common/context/nawy-passport.provider";
import { LeadRowProps } from "@/modules/leads/types";

const useEditAssignedUser = ({ leadRow }: LeadRowProps = {}) => {
  const { lead } = useContext(LeadPageContext);
  const path = window.location.pathname;

  const { permissions: currentUserPermissions } =
    useContext(NawyPassportContext);

  const [editAssignedUserModalOpen, setEditAssignedUserModalOpen] =
    useState(false);

  const isLeadFresh = isOlderThanXDays(
    new Date(
      lead?.data?.lead?.date_of_last_request ||
        leadRow?.date_of_last_request ||
        ""
    ),
    30
  );

  const CanAssignFreshUsers = userHasPermission(
    {
      name: PermissionsEnum.CAN_ASSIGN_FRESH_USERS_JOURNEY,
      level: PermissionLevel.WRITE,
    },
    currentUserPermissions
  );

  const CanAssignNonAvailableUsers = userHasPermission(
    {
      name: PermissionsEnum.CAN_ASSIGN_NON_AVAILABLE_USERS_JOURNEY,
      level: PermissionLevel.WRITE,
    },
    currentUserPermissions
  );

  const statuses =
    isLeadFresh && !CanAssignFreshUsers
      ? [UserStatus.Active]
      : [UserStatus.Active, UserStatus.Fresh];

  const { assignedUsers, usersLoading, setUsersSearchTerm, usersSearchTerm } =
    useUsersData({
      initialParams: {
        ...(CanAssignNonAvailableUsers ? {} : { isActiveOnly: true }),
        assignToUsersQuery: true,
        statuses: statuses,
        excludeUsersWithBlockAllLeads: true,
      },
      enabled: !!editAssignedUserModalOpen,
      organizationUId:
        lead?.data?.lead?.organization?.uid || leadRow?.organization?.uid,
    });

  const [assignedUserId, setAssignedUserId] = useState<string | undefined>(
    lead?.data?.lead?.user.id || leadRow?.user?.id
  );

  const handleChangeAssignedUser = (value: string) => {
    setAssignedUserId(value);
  };

  const {
    mutateAsync: assignLeadToUser,
    isLoading,
    isError,
    isSuccess,
    error,
  } = useMutation({
    mutationFn: ({
      journeyUId,
      userUId,
    }: {
      journeyUId: string | undefined;
      userUId: string;
    }) => assignLeadV2({ journeyUId, userUId }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["getLead"] });
      queryClient.invalidateQueries({ queryKey: ["getComments"] });
      queryClient.invalidateQueries({ queryKey: ["track"] });
      if (path.includes("/leads") || path.includes("/")) {
        queryClient.invalidateQueries({ queryKey: ["leads"] });
      }

      setEditAssignedUserModalOpen(false);

      showToast({
        type: ToastType.SUCCESS,
        message: "Lead assigned successfully",
        toastId: TOAST_IDS.EDIT_LEAD_SUCCESS,
        options: {
          position: "bottom-left",
          autoClose: 3000,
        },
      });
    },
    onError: (error: {
      response: { data: { message: string; statusCode: number } };
    }) => {
      showToast({
        type: isBulkMoveConflictError(error)
          ? ToastType.WARNING
          : ToastType.ERROR,
        message: isBulkMoveConflictError(error)
          ? BULK_MOVE_LOCK_MESSAGE
          : getErrorMessage(error) || "Error assigning lead",
        toastId: TOAST_IDS.EDIT_LEAD_ERROR,
        options: {
          position: "bottom-left",
          autoClose: 3000,
        },
      });
    },
  });

  const handleAssignLead = async () => {
    if (assignedUserId) {
      await assignLeadToUser({
        journeyUId: lead?.data?.lead?.journey_uid || leadRow?.id,
        userUId: assignedUserId,
      });
      // Invalidate activity timeline queries to trigger refetch
      queryClient.invalidateQueries({ queryKey: ["lead-activity"] });
    }
  };

  const [hasSalesProgress, setHasSalesProgress] = useState(false);

  useEffect(() => {
    if (editAssignedUserModalOpen) {
      const journeyUId = lead?.data?.lead?.journey_uid || leadRow?.id;
      if (journeyUId) {
        checkLeadsSalesProgress([journeyUId as string])
          .then((result) => setHasSalesProgress((result ?? []).length > 0))
          .catch(() => setHasSalesProgress(false));
      }
    } else {
      setHasSalesProgress(false);
    }
  }, [editAssignedUserModalOpen]);

  const toggleEditAssignedUserModal = (isOpen: boolean) => {
    setEditAssignedUserModalOpen(isOpen);
  };

  return {
    assignedUser: {
      usersSearchTerm,
      setUsersSearchTerm,
      usersLoading,
      editAssignedUserModalOpen,
      setEditAssignedUserModalOpen,
      handleChangeAssignedUser,
      handleAssignLead,
      isLoading,
      isError,
      isSuccess,
      error,
      value: lead?.data?.lead?.user.id || leadRow?.user?.id,
      state: assignedUserId,
      data: assignedUsers,
      hasSalesProgress,
    },
    toggleEditAssignedUserModal,
  };
};

export default useEditAssignedUser;
